import { useCallback, useEffect, useRef } from 'react';
import { useHistory, useLocation } from 'react-router-dom';

/** Ionic keeps pages mounted: async UI work belongs to one visit, not its cache. */
export function usePageActivity(pathname: string, scopeKey: string | null = null): {
  isActive: boolean;
  capture: () => () => boolean;
} {
  const history = useHistory();
  const location = useLocation();
  const generation = useRef(0);
  const scope = useRef(scopeKey);
  if (scope.current !== scopeKey) {
    scope.current = scopeKey;
    generation.current++;
  }
  useEffect(() => {
    const invalidate = (): void => { generation.current++; };
    const unlisten = history.listen(invalidate);
    return () => {
      invalidate();
      unlisten();
    };
  }, [history]);
  const capture = useCallback(() => {
    const visit = generation.current;
    return () => visit === generation.current && history.location === location && location.pathname === pathname;
  }, [history, location, pathname]);
  return { isActive: location.pathname === pathname, capture };
}
